/**
 * Utility functions for Team Performance Analysis
 */

/**
 * Parses a date string into a UTC Date object at midnight (00:00:00).
 * Only accepts YYYY-MM-DD format for predictable behavior.
 */
export const parseAsUTC = (dateInput) => {
    if (!dateInput) {
        console.warn('parseAsUTC: No date provided');
        return new Date(NaN);
    }

    // Handle Date objects
    if (dateInput instanceof Date) {
        if (isNaN(dateInput.getTime())) {
            console.error('parseAsUTC: Invalid Date object');
            return new Date(NaN);
        }

        // Extract LOCAL calendar date → Create UTC midnight
        // Jan 8 LOCAL → Jan 8 UTC (not Jan 7!)
        return new Date(Date.UTC(
            dateInput.getFullYear(),
            dateInput.getMonth(),
            dateInput.getDate(),
            0, 0, 0, 0
        ));
    }

    // Handle YYYY-MM-DD strings
    if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        const date = new Date(`${dateInput}T00:00:00Z`);

        if (isNaN(date.getTime())) {
            console.error('parseAsUTC: Invalid date string', dateInput);
            return new Date(NaN);
        }

        return date;
    }

    console.error('parseAsUTC: Unsupported format', dateInput);
    return new Date(NaN);
};
/**
 * Counts working days (excluding weekends) between start and end dates (inclusive).
 */
export const countWorkingDays = (start, end, weekendDays = [5, 6]) => {
    const startDateObj = parseAsUTC(start);
    const endDateObj = parseAsUTC(end);

    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
        console.warn('countWorkingDays: Invalid date(s)', { start, end });
        return 0;
    }

    // Prevent nonsensical results
    if (startDateObj > endDateObj) {
        console.warn('countWorkingDays: Start date after end date', { start, end });
        return 0;
    }

    let count = 0;
    let current = new Date(startDateObj);

    while (current <= endDateObj) {
        const day = current.getUTCDay();
        if (!weekendDays.includes(day)) {
            count++;
        }
        current.setUTCDate(current.getUTCDate() + 1);
    }

    return count;
};

/**
 * Color coding for utilization percentages.
 */
export const getUtilizationColor = (utilization) => {
    if (utilization < 60) return '#ef4444'; // Red (Underutilized - Critical)
    if (utilization < 80) return '#3b82f6'; // Blue (Normal)
    if (utilization <= 100) return '#10b981'; // Green (Good)
    return '#f59e0b'; // Amber (Overworked - Warning)
};

/**
 * Sums weekly_capacities entries whose week_start_date falls in [start, end] inclusive.
 */
const sumCapacityInRange = (weeklyCapacities = [], start, end) => {
    const startUTC = parseAsUTC(start);
    const endUTC = parseAsUTC(end);
    if (isNaN(startUTC.getTime()) || isNaN(endUTC.getTime())) return 0;

    return weeklyCapacities.reduce((sum, wc) => {
        const wkUTC = parseAsUTC(wc.week_start_date);
        if (isNaN(wkUTC.getTime())) return sum;
        if (wkUTC >= startUTC && wkUTC <= endUTC) {
            return sum + Number(wc.hours || 0);
        }
        return sum;
    }, 0);
};

/**
 * Builds a map: jiraProjectKey -> { inTab: bool, projectId, developers: [{name, jira_account_id, capacityInRange}] }
 * filteredProjectKeys: array of jira keys from the dashboard filter
 * allTabProjects: array from GET /projects
 * developersByProjectId: { [projectId]: [developer, ...] }  (results of GET /projects/:id/developers)
 */
export const buildProjectCapacityIndex = (filteredProjectKeys, allTabProjects, developersByProjectId, start, end, failedProjectKeys = []) => {
    const tabProjectsByKey = new Map();
    (allTabProjects || []).forEach(p => {
        if (p.jira_project_key) tabProjectsByKey.set(p.jira_project_key, p);
    });

    const failedSet = new Set(failedProjectKeys);
    const index = new Map();
    (filteredProjectKeys || []).forEach(key => {
        const tabProject = tabProjectsByKey.get(key);
        if (!tabProject) {
            index.set(key, { inTab: false, projectId: null, developers: [], fetchFailed: false });
            return;
        }
        const devs = developersByProjectId[tabProject.id] || [];
        const enriched = devs.map(d => ({
            name: d.name,
            jira_account_id: d.jira_account_id,
            capacityInRange: sumCapacityInRange(d.weekly_capacities, start, end),
        }));
        index.set(key, {
            inTab: true,
            projectId: tabProject.id,
            developers: enriched,
            fetchFailed: failedSet.has(key),
        });
    });
    return index;
};

/**
 * Resolves a worklog author to a capacity contribution from the project capacity index.
 * Returns { capacityHours, matched } where capacityHours is summed across in-tab projects
 * the author appears in, plus fallback (workingDays*8) for each out-of-tab project they have logs in.
 */
const resolveAvailableHoursForMember = (memberName, memberAccountId, projectKeysWorkedOn, capacityIndex, workingDays) => {
    let total = 0;
    let hasInTabProject = false;
    let outOfTabProjectCount = 0;

    projectKeysWorkedOn.forEach(pkey => {
        const entry = capacityIndex.get(pkey);
        if (!entry) {
            // Project not in the dashboard filter — shouldn't happen if filter is applied,
            // but fall back gracefully
            outOfTabProjectCount += 1;
            return;
        }
        if (!entry.inTab) {
            outOfTabProjectCount += 1;
            return;
        }
        hasInTabProject = true;
        const dev = entry.developers.find(d =>
            (memberName && d.name === memberName) ||
            (memberAccountId && d.jira_account_id === memberAccountId)
        );
        if (dev) total += dev.capacityInRange;
        // If member not in developers list of an in-tab project, contribute 0 for that project
    });

    total += outOfTabProjectCount * workingDays * 8;

    return { capacityHours: total, hasInTabProject, outOfTabProjectCount };
};

/**
 * Computes aggregated team data from raw worklogs.
 * If capacityIndex is provided, uses it to compute per-member available hours
 * with mixed in-tab (from capacity) and out-of-tab (40/week fallback) handling.
 */
export const computeTeamDataFromWorklogs = (logs, start, end, capacityIndex = null) => {
    const workingDays = countWorkingDays(start, end);
    const membersMap = new Map();

    logs.forEach(log => {
        const member = log.author || 'Unknown';
        if (!membersMap.has(member)) {
            membersMap.set(member, {
                name: member,
                jira_account_id: log.author_id || null,
                total_hours: 0,
                available_hours: workingDays * 8,
                utilization_percentage: 0,
                issues: new Map(),
                worklog_entries: 0,
                logs: [],
                projectKeysWorkedOn: new Set(),
            });
        }

        const m = membersMap.get(member);
        const hrs = Number(log.loggedHours || 0);
        m.total_hours += hrs;
        m.worklog_entries += 1;
        m.logs.push(log);
        if (log.issue_project_key) m.projectKeysWorkedOn.add(log.issue_project_key);
        if (!m.jira_account_id && log.author_id) m.jira_account_id = log.author_id;

        const issueKey = log.issue_key || 'UNKNOWN-ISSUE';
        if (!m.issues.has(issueKey)) {
            m.issues.set(issueKey, {
                key: issueKey,
                status: log.issue_status,
                priority: log.issue_priority,
                summary: log.issue_summary,
                logged_hours: 0,
                estimated_hours: Number(log.estimatedHours || 0),
                work_descriptions: [],
            });
        }

        const issue = m.issues.get(issueKey);
        issue.logged_hours += hrs;
        if (log.comment) issue.work_descriptions.push(log.comment);
    });

    const team_members = Array.from(membersMap.values()).map(m => {
        const issues = Array.from(m.issues.values()).map(issue => ({
            ...issue,
            variance: issue.logged_hours - issue.estimated_hours
        }));

        // Compute available_hours: from capacity index if provided, else fallback
        let availableHours = workingDays * 8;
        if (capacityIndex && capacityIndex.size > 0) {
            const projectKeysArr = Array.from(m.projectKeysWorkedOn);
            const { capacityHours, hasInTabProject } = resolveAvailableHoursForMember(
                m.name,
                m.jira_account_id,
                projectKeysArr,
                capacityIndex,
                workingDays
            );
            // If member has at least one in-tab project, use computed capacity (which may include fallback portions).
            // If they have zero in-tab projects, capacityHours == fallback total already.
            availableHours = hasInTabProject || capacityHours > 0 ? capacityHours : workingDays * 8;
        }

        const utilization = availableHours > 0 ? (m.total_hours / availableHours) * 100 : 0;

        // Performance metrics
        const total_logged = issues.reduce((s, it) => s + (it.logged_hours || 0), 0);
        const total_estimated = issues.reduce((s, it) => s + (it.estimated_hours || 0), 0);
        const issues_with_estimates = issues.filter(it => (it.estimated_hours || 0) > 0).length;
        const accuracy_percentage = total_estimated > 0 ? ((total_logged / total_estimated) * 100) : 0;

        // Collaboration rate where author != assignee
        const total_logs = m.logs.length;
        const helping_logs = m.logs.filter(l => l.author && l.issue_assignee && l.author !== l.issue_assignee).length;
        const collaboration_rate = total_logs > 0 ? (helping_logs / total_logs) * 100 : 0;

        return {
            name: m.name,
            total_hours: m.total_hours,
            available_hours: availableHours,
            utilization_percentage: utilization,
            issues_worked_on: issues.length,
            issues,
            worklog_entries: m.worklog_entries,
            estimation_accuracy: {
                accuracy_percentage: Number(accuracy_percentage.toFixed(2)),
                total_logged: Number(total_logged.toFixed(2)),
                total_estimated: Number(total_estimated.toFixed(2)),
                variance: Number((total_logged - total_estimated).toFixed(2)),
                issues_with_estimates
            },
            collaboration_rate: Number(collaboration_rate.toFixed(2)),
        };
    });

    const totalAvailable = team_members.reduce((s, tm) => s + tm.available_hours, 0);

    // Build warnings for projects not in the Projects tab
    const fallbackProjects = [];
    const failedProjects = [];
    if (capacityIndex && capacityIndex.size > 0) {
        capacityIndex.forEach((entry, key) => {
            if (!entry.inTab) fallbackProjects.push(key);
            if (entry.fetchFailed) failedProjects.push(key);
        });
    }

    const result = {
        period: { start_date: start, end_date: end, working_days: workingDays },
        available_hours: totalAvailable,
        team_members,
        ai_summary: null,
        capacity_warning: {
            no_filter: !capacityIndex || capacityIndex.size === 0,
            fallback_projects: fallbackProjects,
            failed_projects: failedProjects,
        },
    };
    return result;
};
